/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { TutorialStep } from '@/components/TaskTutorial';
import type { NgGenericTask, MicrotaskInput } from '@karya/core';

/**
 * Task specification with the following flow:
 * 1. START
 * 2. Platform: Task Explainer (text with max_lines=3)
 * 3. Platform: Text Test - Collapsible (text with collapsible=true)
 * 4. Platform: Text Test - Max Lines (text with max_lines=5)
 * 5. Platform: Text Test - Both (text with max_lines=4 and collapsible=true)
 * 6. Platform: LLM Prompt (text)
 * 7. User: PRP Text Comparison (user selects which LLM response is better)
 * 8. Platform: Feedback Question 1
 * 9. User: MCQ 1
 * 10. Platform: Feedback Question 2
 * 11. User: MCQ 2
 * 12. Platform: Feedback Question 3
 * 13. User: MCQ 3
 * 14. END
 */
export const mockTaskSpec: NgGenericTask = {
  start: {
    key: 'start',
    name: 'Start',
    description: 'First component of the task',
    ctype: 'START',
    next: { type: 'REF', src: 'CONSTANT', value: 'platform_task_explainer' },
  },

  // Platform: Task Explainer
  platform_task_explainer: {
    key: 'platform_task_explainer',
    ctype: 'PLATFORM',
    dtype: 'TEXT',
    text: {
      type: 'STRING',
      src: 'CONSTANT',
      value:
        'Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions. Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.Welcome! In this task, you will compare two LLM responses and provide feedback. You will see a prompt and two different responses from two language models. Please select which response you prefer, and then answer a few feedback questions.',
    },
    type: { type: 'ENUM', src: 'CONSTANT', value: 'AUTO', options: { AUTO: 'Auto', MANUAL: 'Manual', DELAY: 'Delay' } },
    max_lines: { type: 'INT', src: 'CONSTANT', value: 3 },
    next: { type: 'REF', src: 'CONSTANT', value: 'platform_text_test_collapsible' },
  },

  // Platform: Text with collapsible flag (test case)
  platform_text_test_collapsible: {
    key: 'platform_text_test_collapsible',
    ctype: 'PLATFORM',
    dtype: 'TEXT',
    text: {
      type: 'STRING',
      src: 'CONSTANT',
      value:
        'This is a test of the collapsible text feature. When the collapsible flag is set to true, the text should show a "Read More" toggle. This allows users to expand or collapse long content. The collapsible feature is useful for displaying lengthy instructions, terms and conditions, or detailed explanations without overwhelming the user interface. When collapsed, users see a preview and can choose to expand for the full content. This improves the user experience by keeping the interface clean and organized while still providing access to all necessary information when needed.',
    },
    type: { type: 'ENUM', src: 'CONSTANT', value: 'AUTO', options: { AUTO: 'Auto', MANUAL: 'Manual', DELAY: 'Delay' } },
    collapsable: { type: 'BOOLEAN', src: 'CONSTANT', value: true },
    next: { type: 'REF', src: 'CONSTANT', value: 'platform_text_test_maxlines' },
  },

  // Platform: Text with max_lines only (test case)
  platform_text_test_maxlines: {
    key: 'platform_text_test_maxlines',
    ctype: 'PLATFORM',
    dtype: 'TEXT',
    text: {
      type: 'STRING',
      src: 'CONSTANT',
      value:
        'This text component has max_lines set to 5, which should automatically enable the read more toggle. The max_lines parameter controls how many lines of text are shown before the content is truncated. This is useful for maintaining a consistent layout while still allowing users to access the full content when needed. The read more toggle will appear automatically when max_lines is specified, even without explicitly setting the collapsible flag. This makes it easy to create clean, organized interfaces with long-form content.',
    },
    type: { type: 'ENUM', src: 'CONSTANT', value: 'AUTO', options: { AUTO: 'Auto', MANUAL: 'Manual', DELAY: 'Delay' } },
    max_lines: { type: 'INT', src: 'CONSTANT', value: 5 },
    next: { type: 'REF', src: 'CONSTANT', value: 'platform_text_test_both' },
  },

  // Platform: Text with both max_lines and collapsible (test case)
  platform_text_test_both: {
    key: 'platform_text_test_both',
    ctype: 'PLATFORM',
    dtype: 'TEXT',
    text: {
      type: 'STRING',
      src: 'CONSTANT',
      value:
        'This text component has both max_lines set to 4 and collapsible set to true. This demonstrates how both parameters work together. The max_lines parameter will limit the preview to 4 lines, and the collapsible flag ensures the read more toggle is displayed. This combination provides fine-grained control over how the text is displayed. Users will see a 4-line preview and can expand to see the full content. This is particularly useful for long-form content like terms of service, detailed instructions, or comprehensive explanations that need to be accessible but not overwhelming.',
    },
    type: { type: 'ENUM', src: 'CONSTANT', value: 'AUTO', options: { AUTO: 'Auto', MANUAL: 'Manual', DELAY: 'Delay' } },
    max_lines: { type: 'INT', src: 'CONSTANT', value: 4 },
    collapsable: { type: 'BOOLEAN', src: 'CONSTANT', value: true },
    next: { type: 'REF', src: 'CONSTANT', value: 'platform_llm_prompt' },
  },

  // Platform: LLM Prompt
  platform_llm_prompt: {
    key: 'platform_llm_prompt',
    ctype: 'PLATFORM',
    dtype: 'TEXT',
    text: {
      type: 'STRING',
      src: 'CONSTANT',
      value: 'Prompt: "Explain the concept of machine learning in simple terms."',
    },
    type: { type: 'ENUM', src: 'CONSTANT', value: 'AUTO', options: { AUTO: 'Auto', MANUAL: 'Manual', DELAY: 'Delay' } },
    next: { type: 'REF', src: 'CONSTANT', value: 'user_prp_comparison' },
  },

  // User: PRP Text Comparison
  user_prp_comparison: {
    key: 'user_prp_comparison',
    ctype: 'USER',
    dtype: 'PRP_TEXT_COMPARISON',
    model_a_name: { type: 'STRING', src: 'CONSTANT', value: 'GPT-4' },
    model_a_response: {
      type: 'STRING',
      src: 'CONSTANT',
      value:
        "Machine learning is a way for computers to learn from data without being explicitly programmed. Think of it like teaching a child: instead of giving them every single rule, you show them examples and they figure out the patterns themselves. The computer looks at lots of examples, finds patterns, and uses those patterns to make predictions or decisions about new data it hasn't seen before.",
    },
    model_b_name: { type: 'STRING', src: 'CONSTANT', value: 'Claude 3.7' },
    model_b_response: {
      type: 'STRING',
      src: 'CONSTANT',
      value:
        "Machine learning is a subset of artificial intelligence where algorithms enable systems to automatically learn and improve from experience. Rather than following pre-programmed instructions, these systems analyze large datasets to identify patterns, make predictions, and adapt their behavior. It's essentially teaching computers to recognize patterns and make decisions based on data, similar to how humans learn from experience. Machine learning is a subset of artificial intelligence where algorithms enable systems to automatically learn and improve from experience. Rather than following pre-programmed instructions, these systems analyze large datasets to identify patterns, make predictions, and adapt their behavior. It's essentially teaching computers to recognize patterns and make decisions based on data, similar to how humans learn from experience.Machine learning is a subset of artificial intelligence where algorithms enable systems to automatically learn and improve from experience. Rather than following pre-programmed instructions, these systems analyze large datasets to identify patterns, make predictions, and adapt their behavior. It's essentially teaching computers to recognize patterns and make decisions based on data, similar to how humans learn from experience.Machine learning is a subset of artificial intelligence where algorithms enable systems to automatically learn and improve from experience. Rather than following pre-programmed instructions, these systems analyze large datasets to identify patterns, make predictions, and adapt their behavior. It's essentially teaching computers to recognize patterns and make decisions based on data, similar to how humans learn from experience.Machine learning is a subset of artificial intelligence where algorithms enable systems to automatically learn and improve from experience. Rather than following pre-programmed instructions, these systems analyze large datasets to identify patterns, make predictions, and adapt their behavior. It's essentially teaching computers to recognize patterns and make decisions based on data, similar to how humans learn from experience.Machine learning is a subset of artificial intelligence where algorithms enable systems to automatically learn and improve from experience. Rather than following pre-programmed instructions, these systems analyze large datasets to identify patterns, make predictions, and adapt their behavior. It's essentially teaching computers to recognize patterns and make decisions based on data, similar to how humans learn from experience.Machine learning is a subset of artificial intelligence where algorithms enable systems to automatically learn and improve from experience. Rather than following pre-programmed instructions, these systems analyze large datasets to identify patterns, make predictions, and adapt their behavior. It's essentially teaching computers to recognize patterns and make decisions based on data, similar to how humans learn from experience.",
    },
    randomize_order: { type: 'BOOLEAN', src: 'CONSTANT', value: true },
    anonymize_model_names: { type: 'BOOLEAN', src: 'CONSTANT', value: true },
    selected_model: { type: 'STRING', key: 'selected_model' },
    next: { type: 'REF', src: 'CONSTANT', value: 'platform_feedback_q1' },
  },

  // Platform: Feedback Question 1
  platform_feedback_q1: {
    key: 'platform_feedback_q1',
    ctype: 'PLATFORM',
    dtype: 'TEXT',
    text: {
      type: 'STRING',
      src: 'CONSTANT',
      value: 'Question 1: How clear and understandable was the response you selected?',
    },
    type: { type: 'ENUM', src: 'CONSTANT', value: 'AUTO', options: { AUTO: 'Auto', MANUAL: 'Manual', DELAY: 'Delay' } },
    next: { type: 'REF', src: 'CONSTANT', value: 'user_mcq_1' },
  },

  // User: MCQ 1
  user_mcq_1: {
    key: 'user_mcq_1',
    ctype: 'USER',
    dtype: 'MCQ',
    options: {
      type: 'STRING_ARRAY',
      src: 'CONSTANT',
      value: ['Very Clear', 'Clear', 'Somewhat Clear', 'Unclear', 'Very Unclear'],
    },
    min: { type: 'INT', src: 'CONSTANT', value: 1 },
    max: { type: 'INT', src: 'CONSTANT', value: 1 },
    type: {
      type: 'ENUM',
      src: 'CONSTANT',
      value: 'VERTICAL',
      options: { VERTICAL: 'Vertical', HORIZONTAL: 'Horizontal', WORD_CLOUD: 'Word Cloud' },
    },
    enableOther: { type: 'BOOLEAN', src: 'CONSTANT', value: false },
    selected: { type: 'STRING_ARRAY', key: 'mcq_1_selected' },
    next: { type: 'REF', src: 'CONSTANT', value: 'platform_feedback_q2' },
  },

  // Platform: Feedback Question 2
  platform_feedback_q2: {
    key: 'platform_feedback_q2',
    ctype: 'PLATFORM',
    dtype: 'TEXT',
    text: {
      type: 'STRING',
      src: 'CONSTANT',
      value: 'Question 2: How accurate and informative was the response?',
    },
    type: { type: 'ENUM', src: 'CONSTANT', value: 'AUTO', options: { AUTO: 'Auto', MANUAL: 'Manual', DELAY: 'Delay' } },
    next: { type: 'REF', src: 'CONSTANT', value: 'user_mcq_2' },
  },

  // User: MCQ 2
  user_mcq_2: {
    key: 'user_mcq_2',
    ctype: 'USER',
    dtype: 'MCQ',
    options: {
      type: 'STRING_ARRAY',
      src: 'CONSTANT',
      value: ['Very Accurate', 'Accurate', 'Somewhat Accurate', 'Inaccurate', 'Very Inaccurate'],
    },
    min: { type: 'INT', src: 'CONSTANT', value: 1 },
    max: { type: 'INT', src: 'CONSTANT', value: 1 },
    type: {
      type: 'ENUM',
      src: 'CONSTANT',
      value: 'VERTICAL',
      options: { VERTICAL: 'Vertical', HORIZONTAL: 'Horizontal', WORD_CLOUD: 'Word Cloud' },
    },
    enableOther: { type: 'BOOLEAN', src: 'CONSTANT', value: false },
    selected: { type: 'STRING_ARRAY', key: 'mcq_2_selected' },
    next: { type: 'REF', src: 'CONSTANT', value: 'platform_feedback_q3' },
  },

  // Platform: Feedback Question 3
  platform_feedback_q3: {
    key: 'platform_feedback_q3',
    ctype: 'PLATFORM',
    dtype: 'TEXT',
    text: {
      type: 'STRING',
      src: 'CONSTANT',
      value: 'Question 3: Would you use this response in a real-world scenario?',
    },
    type: { type: 'ENUM', src: 'CONSTANT', value: 'AUTO', options: { AUTO: 'Auto', MANUAL: 'Manual', DELAY: 'Delay' } },
    next: { type: 'REF', src: 'CONSTANT', value: 'user_mcq_3' },
  },

  // User: MCQ 3
  user_mcq_3: {
    key: 'user_mcq_3',
    ctype: 'USER',
    dtype: 'MCQ',
    options: {
      type: 'STRING_ARRAY',
      src: 'CONSTANT',
      value: ['Definitely Yes', 'Probably Yes', 'Maybe', 'Probably No', 'Definitely No'],
    },
    min: { type: 'INT', src: 'CONSTANT', value: 1 },
    max: { type: 'INT', src: 'CONSTANT', value: 1 },
    type: {
      type: 'ENUM',
      src: 'CONSTANT',
      value: 'VERTICAL',
      options: { VERTICAL: 'Vertical', HORIZONTAL: 'Horizontal', WORD_CLOUD: 'Word Cloud' },
    },
    enableOther: { type: 'BOOLEAN', src: 'CONSTANT', value: false },
    selected: { type: 'STRING_ARRAY', key: 'mcq_3_selected' },
    next: { type: 'REF', src: 'CONSTANT', value: 'end' },
  },

  // END
  end: {
    key: 'end',
    name: 'End',
    description: 'Last component of the task',
    ctype: 'END',
    next: { type: 'REF', src: 'CONSTANT', value: null },
  },
};

/**
 * Mock microtask input data
 * For this task spec, we don't need any MT_INPUT references since all data is CONSTANT
 */
export const mockMtInput: MicrotaskInput = {
  data: {},
  files: {},
};

export const evalsTutorialSteps: TutorialStep[] = [
  {
    title: 'Select your prompt',
    description:
      'Begin by choosing a prompt — a question, topic, or instruction — that you want the AI models to respond to. This prompt acts as the starting point for generating answers and can be as simple or complex as you like. You can either type your own custom prompt or pick from a set of curated sample prompts provided on the platform.',
    stepNumber: 1,
  },
  {
    title: 'Compare responses from two AI Models',
    description:
      'After you submit your prompt, two different AI models will generate responses for you. Each model’s answer will appear side-by-side, allowing you to review and compare them based on quality, clarity, creativity, and accuracy. Take your time to read both responses carefully — the goal is to identify which model better captures what you were expecting.',
    stepNumber: 2,
  },
  {
    title: 'Submit your preference',
    description:
      'Select your preferred answers generated by AI Models for you entered prompt. Also select why you preferred that answer.',
    stepNumber: 3,
  },
];
